// Path: src/components/aols/aeo/LightScience/LightScience.tsx
// Updated to remove duplicate footer and enhance SEO for Sun Light Meditation education
// Ensures single footer rendering in PublicLayout.tsx
import React, { useState, useEffect, useCallback } from "react";
import { Layout, Button, Typography, Collapse } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import logo from "/logo.jpg";
import "./LightScience.css";

const { Title, Paragraph } = Typography;
const { Panel } = Collapse;

// Define types for the milestones data
interface Event {
  text: string;
  link?: string;
  linkText?: string;
}

interface Milestone {
  year: string;
  events: Event[];
}

// Define the type for the dimensions state
interface Dimensions {
  width: number;
  height: number;
}

const milestonesData: Milestone[] = [
  {
    year: "2025",
    events: [
      {
        text: "Q2: OLS health , OLS Referral", 
        link: "https://www.olsme.com/OLShealth",
      },
      {
        text: "Q1: 2855.OLS Recommendation letter to the USA President",
        link: "https://onelightsystem.blogspot.com/2025/01/2830ols-ieeaeo-amba-delivering.html",
      },
    ],
  },
  {
    year: "2024",
    events: [
      {
        text: "Q2: 2516.OLS Recommendation letter to the French President",
        link: "https://onelightsystem.blogspot.com/2024/03/2516ols-ieeaeo-amba-sends-letter-to.html",
      },
      {
        text: "Q3: Niederlassung Deutschland München Recommendation letter to the Germany President",
        link: "https://onelightsystem.blogspot.com/2024/07/2670ols-ieeaeo-amba-delivers-letter.html",
      },
      {
        text: "Q4: OLS web upgrade developmnet ",
        link: "www.ols-med.net",
      },
    ],
  },
  {
    year: "2023",
    events: [
      {
        text: "2260.OLS Recommendations to the France Government",
        link: "https://onelightsystem.blogspot.com/2023/07/2260ols-ieeaeo-astel4-eae-amba.html",
      },
      {
        text: "2391.OLS Visitation Taiwan with Recommendation to the Taiwan Government",
        link: "https://onelightsystem.blogspot.com/2023/11/2391ols-ieeaeo-delivers-e.html",
      },
    ],
  },
  {
    year: "2022",
    events: [
      {
        text: "Q4: New Location in Egypt, Red Sea with Recommendation Letter to the Egypt President",
      },
    ],
  },
  {
    year: "2021",
    events: [
      {
        text: "Q3: Attended Mt. Shasta CA City Council Regular Meeting",
        link: "https://mtshastaca.gov/wp-content/uploads/2021/06/6.28.2021-Agenda-Packet.pdf",
        linkText: "comment page 4 recommendation",
      },
      {
        text: "Recommendation to the CA SWRC State Water Board Meeting",
        link: "https://www.waterboards.ca.gov/public_notices/comments/caltrans_progress_report/nazar_kosmo.pdf",
        linkText: "Caltrans TMDL Progress Report",
      },
    ],
  },
  {
    year: "2020",
    events: [
      {
        text: "Launching Live Public Enlightenment Series",
        link: "https://youtu.be/NEjvhBAiHvs?si=e0BQrIVmO8wo4L6q",
      },
      {
        text: "1281.OLS Recommendation to the USA President",
        link: "https://onelightsystem.blogspot.com/2020/10/1281ols-ieeaeo-amba-delivers.html",
      },
    ],
  },
  {
    year: "2019",
    events: [{ text: "OLS iee. Developed: A proper time measurement and Light Calendar" }],
  },
  {
    year: "2018",
    events: [
      { text: "Q1: OLS iee.aeo (science and consulting undergraduate departments established)" },
      { text: "Q2: OLSME technology introduced" },
      { text: "Q3: ONELIGHTSYSTEM (OLS) Registered as an LLC in California" },
      { text: "Q4: New dedicated web portal developed" },
      {
        text: "CA SWRSB Public Comment Caltrans TMDL Progress Report",
        link: "https://www.waterboards.ca.gov/public_notices/comments/caltrans_progress_report/nazar_kosmo.pdf",
      },
    ],
  },
  {
    year: "2017",
    events: [
      {
        text: "Q4: 67 new accounts, over 100 trials, and a new world record set.",
        link: "https://recordsetter.com/user/Asvitloaten",
        linkText: '"200 days"',
      },
      {
        text: "Q3: Recommendation to the CA Water Board Meeting - Item 4 Emergency Water Conservation",
        link: "https://www.waterboards.ca.gov/board_info/agendas/2017/aug/nazar_kosmo.pdf",
      },
      { text: "Q2: Initial OLSM prototype designed." },
      {
        text: "Public Workshop Prohibiting Wasteful Water Use Practices Recommendation",
        link: "https://www.waterboards.ca.gov/water_issues/programs/conservation_portal/comments20171226/docs/nazar_kosmo.pdf",
      },
      { text: "04.22.2017: Founded by Nazar Pankiv (Asvitloaten) in Foster city, CA" },
    ],
  },
];

const LightScience: React.FC = () => {
  const [dimensions, setDimensions] = useState<Dimensions>({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => setDimensions({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  return (
    <Layout
      className="full-height-layout"
      style={{
        background: "#000000",
        maxWidth: dimensions.width > 768 ? "1200px" : "100%",
        margin: "0 auto",
      }}
    >
      <Helmet>
        <title>About OLS | Sun Light Meditation & Spiritual Growth Academy</title>
        <meta
          name="description"
          content="Explore onelightsystem OLS, a Meditation Academy founded by Nazar Pankiv (Asvitloaten). Join our Sun Light Meditation programs, discover spiritual growth, and learn about our global events, including recommendation letters to world leaders for a Light Civilization. Start your journey today!"
        />
        <meta
          name="keywords"
          content="onelightsystem OLS, Sun Light Meditation, Meditation Academy, spiritual growth, Nazar Pankiv, Asvitloaten, Light Civilization, OLS iee.aeo, meditation education, global meditation events, recommendation letters, USA President, French President, Germany President, Taiwan Government, Egypt President, California Water Board"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="OneLightSystem OLS" />
        <link rel="canonical" href="https://www.olsme.com/lightScience" />
        <meta property="og:title" content="About OLS | Sun Light Meditation & Spiritual Growth Academy" />
        <meta
          property="og:description"
          content="Join onelightsystem OLS, founded by Nazar Pankiv, for Sun Light Meditation and spiritual growth. Explore our global events and programs for a Light Civilization."
        />
        <meta property="og:url" content="https://www.olsme.com/lightScience" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About OLS | Sun Light Meditation & Spiritual Growth Academy" />
        <meta
          name="twitter:description"
          content="Join OLS for Sun Light Meditation and spiritual growth. Founded by Nazar Pankiv, our academy offers global events and programs for a Light Civilization."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <script type="application/ld+json">
          {JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "EducationalOrganization",
              "name": "onelightsystem OLS",
              "alternateName": "OLS Aunselo",
              "url": "https://www.olsme.com/lightScience",
              "description": "OneLightSystem OLS is a Meditation Academy offering spiritual education through Sun Light Meditation, guiding students toward personal growth and a Light Civilization vision.",
              "founder": { "@type": "Person", "name": "Nazar Pankiv", "alternateName": "Asvitloaten" },
              "address": { "@type": "PostalAddress", "addressLocality": "Belmont", "addressRegion": "CA", "addressCountry": "US" },
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "customer service",
                "email": "contact@olsme.com",
              },
              "sameAs": [
                "https://www.linkedin.com/company/onelightsystem",
                "https://twitter.com/onelightsystem",
                "https://www.facebook.com/onelightsystem",
                "https://www.instagram.com/onelightsystem",
                "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
              ],
            },
            {
              "@context": "https://schema.org",
              "@type": "Course",
              "name": "Sun Light Meditation Program",
              "description": "A comprehensive meditation education program by onelightsystem OLS, focusing on spiritual growth and personal transformation through Sun Light Meditation techniques.",
              "provider": {
                "@type": "EducationalOrganization",
                "name": "onelightsystem OLS",
                "url": "https://www.olsme.com"
              },
              "url": "https://www.olsme.com/register/olssregister",
              "hasCourseInstance": {
                "@type": "CourseInstance",
                "courseMode": "online",
                "instructor": {
                  "@type": "Person",
                  "name": "Nazar Pankiv",
                  "alternateName": "Asvitloaten"
                }
              }
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.olsme.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "About",
                  "item": "https://www.olsme.com/lightScience"
                }
              ]
            }
          ])}
        </script>
      </Helmet>

      <section className="content-section">
        <div className="content">
          <a href="https://www.olsme.com" onClick={handleLogoClick} aria-label="Navigate to onelightsystem OLS homepage">
            <img className="logo" src={logo} alt="OneLightSystem OLS Logo - Meditation Academy" loading="lazy" />
          </a>

          <Title level={1} className="section-title">onelightsystem OLS</Title>
          
          <Title level={2} className="subsection-title">Meditation Education for Spiritual Development</Title>
          <div className="button-row">
            <Button className="nav-button">
              <Link to="/OLSaeo" aria-label="Explore OLS iee.aeo Department">OLS iee.aeo</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/Stat" aria-label="View OLS student statistics">OLS student</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/aOLSLMasset" aria-label="Explore OLSLM assets">OLSLM assets</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/olsme" aria-label="Learn about olsme">olsme</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/OLSinventions" aria-label="Discover OLS inventions">OLS inventions</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/OLShealth" aria-label="Explore OLS health initiatives">OLS health</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/venus" aria-label="Learn about Venus Mission">Venus Mission</Link>
            </Button>
          </div>

          <div className="feature-card">
            <section className="about">
              <Title level={2} className="section-title">About OLS</Title>
              <Paragraph className="content-text">
                Founded by Nazar Pankiv (Asvitloaten) on April 22, 2017, in Belmont, California,
                onelightsystem OLS is a Meditation Education Academy dedicated to spiritual development
                through Sun Light Meditation. We guide students from foundational practices to mastery,
                fostering personal growth on all levels—mental, physical, and spiritual.
              </Paragraph>
              <Paragraph className="content-text">
                With an innovative approach, OLS empowers individuals with cutting-edge tools and holistic health solutions,
                advancing our vision of a peaceful, progressive Light Civilization. Rooted in clarity, connection, and empowerment,
                we integrate advanced systems for meditation, education, and community improvements to create a brighter future for all.
                Our global events, including recommendation letters to world leaders, reflect our commitment to spiritual growth and societal impact.
              </Paragraph>
            </section>
          </div>

          <div className="feature-card">
            <section className="vision">
              <Title level={2} className="section-title">Our Vision</Title>
              <Paragraph className="content-text">
                OLS envisions a Light Civilization—a future where humanity thrives in harmony with the Sun as life’s source.
                Through Sun Light Meditation, we cultivate enlightenment, illumination, and spiritual growth, paving the way for a connected, peaceful civilization.
                Our journey includes impactful events and recommendations to governments worldwide, promoting a global movement for peace and enlightenment.
              </Paragraph>
            </section>
          </div>

          <div className="feature-card">
            <section className="principles">
              <Title level={2} className="section-title">Our Principles</Title>
              <ul className="styled-list">
                <li><strong>Our Star, Our God:</strong> Honoring the Sun as the ultimate source of life and energy.</li>
                <li><strong>Light Civilization:</strong> Building a progressive, enlightened society through meditation.</li>
                <li><strong>Empowerment:</strong> Equipping individuals with tools for personal and collective transformation.</li>
              </ul>
            </section>
          </div>

          <div className="feature-card">
            <section className="departments">
              <Title level={2} className="section-title">OLS iee.aeo Department</Title>
              <Paragraph className="content-text">
                Our unique undergraduate department, OLS iee.aeo, focuses on Science and Counseling. It blends rigorous scientific research with practical meditation techniques, offering students a robust education to enhance their spiritual and physical well-being. Learn more about this pioneering initiative:
              </Paragraph>
              <Button className="custom-button">
                <Link to="/OLSaeo" aria-label="Learn more about OLS iee.aeo Department">OLS iee.aeo</Link>
              </Button>
            </section>
          </div>

          <div className="feature-card">
            <section className="join">
              <Title level={2} className="section-title">Become an OLS Student</Title>
              <Paragraph className="content-text">
                Joining OLS is a unique opportunity to embark on a transformative journey. Start today and unlock your potential with our global Meditation Academy.
              </Paragraph>
              <div className="button-group">
                <Button className="custom-button">
                  <Link to="/register/olssregister" aria-label="Register to become an OLS student">Become a Student</Link>
                </Button>
                <Button className="custom-button">
                  <Link to="/register/olsmeregister" aria-label="Subscribe to OLSME">OLSME Subscription</Link>
                </Button>
                <Button className="custom-button">
                  <Link to="/register/olslmregister" aria-label="Explore OLSLM Assets">OLSLM Assets</Link>
                </Button>
              </div>
            </section>
          </div>

          <div className="feature-card milestone-box">
            <section className="milestones">
              <Title level={2} className="section-title">Our Journey</Title>
              <Paragraph className="content-text">
                Explore the milestones of onelightsystem OLS, from our founding in 2017 to our global outreach events, including recommendation letters to world leaders and community engagements.
                For more about our upcoming events and activities <Link to="/OlSupdates">OLS updates</Link>.
              </Paragraph>
              <Collapse
                className="milestone-collapse"
                defaultActiveKey={["2025"]}
                aria-label="Timeline of onelightsystem OLS milestones"
              >
                {milestonesData.map((milestone) => (
                  <Panel header={milestone.year} key={milestone.year}>
                    <ul className="styled-list">
                      {milestone.events.map((event, index) => (
                        <li key={index}>
                          {event.link ? (
                            <>
                              {event.text}{" "}
                              <a
                                href={event.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Learn more about ${event.text}`}
                              >
                                {event.linkText || "Learn More"}
                              </a>
                            </>
                          ) : (
                            event.text
                          )}
                        </li>
                      ))}
                    </ul>
                  </Panel>
                ))}
              </Collapse>
            </section>
          </div>
        </div>
      </section>
      {/* Removed <Footer /> to prevent duplication; footer is rendered in PublicLayout.tsx */}
    </Layout>
  );
};

export default LightScience;